import { OnDestroy, OnInit, Component } from '@angular/core';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { DateService } from '../../../../common/services/date.service';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { Router } from '@angular/router';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { tap } from 'rxjs/operators';
import { AdminReportsService } from '../../../services/admin-reports.service';
import { BondSearchCriteria, CertificateOfDepositModel } from '../../../models/admin-reports.model';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { OperationType } from "../../../../common/models/organization.model";

@Component({
    selector: 'app-certificate-of-deposit-report',
    templateUrl: './certificate-of-deposit-report.component.html',
    styleUrls: ['./certificate-of-deposit-report.component.scss'],
    standalone: false
})
export class CertificateOfDepositReportComponent implements OnInit, OnDestroy {
    activeBonds: CertificateOfDepositModel[]
    gridOptions: GridOptions;
	gridApi?: GridApi;
    subHeader: SubHeader = new SubHeader({
        title: "Certificate of Deposit Tracking",
        buttons: [{ name: "Run Report", method: () => this.generateReport() }, { name: "Download Report", method: () => this.downloadReport() }]
    });

    subscription: Subscription = new Subscription();

    constructor(
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        private readonly faService: AdminReportsService,
        public router: Router,
        public subHeaderService: SubHeaderService
	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
        this.gridOptions.suppressColumnVirtualisation = true;
		this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.generateReport();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    formatOwnerType(): any {
        return (obj: any) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["ownerType"] != null) {
                let isBrineHaulerFunc = this.formatService.flagEnumFormatter("ownerType", OperationType, "BrineHauler", true);
                let isOperatorFunc = (this.formatService.flagEnumFormatter("ownerType", OperationType, "Operator", true));
                let isInjectionOperatorFunc = (this.formatService.flagEnumFormatter("ownerType", OperationType, "InjectionOperator", true));
                if (isBrineHaulerFunc(obj) == true) {
                    returnValue = "Brine Hauler";
                }
                if (isOperatorFunc(obj) == true || isInjectionOperatorFunc(obj) == true) {
                    if (returnValue.length > 0) returnValue += ", ";
                    returnValue += "Well Owner";
                }
            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }

    generateReport() {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }

        const criteria = new BondSearchCriteria();
        criteria.isActive = true;

        this.subscription = this.faService.getCertificateOfDeposits(criteria).pipe(
            tap((data) => {
                this.activeBonds = data;
            })).subscribe({
                next: (success) => {
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.activeBonds);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                }, 
                error: (fail) => {
                    console.error(fail);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                }
            });
    };

    downloadReport() {
        const now = new Date();
        const frmDt = this.datePipe.transform(now, "yyyy-MM-dd:hh:mm").toString();
        const params = {
            skipHeader: false,
            allColumns: true,
            fileName: `CDTracking_${frmDt.toString()}} `,
            sheetName: "Certificate of Deposit",
            exportMode: "xlsx"
        }

        this.formatService.exportDataToExcel(params.fileName, params.sheetName, this.gridApi);
    }

    public createColumnDefinitions() {
        return [

            {
                headerClass: "wrapped",
                headerName: "Owner Name",
                headerTooltip: "Owner Name",
                field: "ownerName",
                tooltipField: 'ownerName',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 100,
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Owner Number",
                headerTooltip: "Owner Number",
                field: "ownerNumber",
                tooltipField: 'ownerNumber',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams, 
                minWidth: 125,
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Owner Type",
                headerTooltip: "Owner Type",
                tooltipValueGetter: this.formatOwnerType(),
                valueGetter: this.formatOwnerType(),
                minWidth: 110,
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Amount",
                headerTooltip: "Amount",
                field: "amount",
                tooltipValueGetter: this.formatService.formatCurrency("amount", "USD", "symbol", "1.2-2"),
                valueFormatter: this.formatService.formatCurrency("amount", "USD", "symbol", "1.2-2"),
                filter: "agNumberColumnFilter",
                filterParams: this.formatService.defaultNumberFilterParams, 
                minWidth: 150,
                maxWidth: 175
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Name",
                headerTooltip: "Bank Name",
                field: "bankName",
                tooltipField: 'bankName',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams, 
                minWidth: 100,
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Address",
                headerTooltip: "Bank Address",
                field: "bankAddress",
                tooltipField: 'bankAddress',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams, 
                minWidth: 100,
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Contact Phone",
                headerTooltip: "Bank Contact Phone",
                field: "bankContactPhoneNumber",
                tooltipField: 'bankContactPhoneNumber',
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams, 
                minWidth: 100,
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Contact Email",
                headerTooltip: "Bank Contact Email",
                valueGetter: this.emailFormatter(),
                tooltipValueGetter: this.emailFormatter(),
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams, 
                minWidth: 100,
                maxWidth: 200
            }, 
            {
                headerClass: "wrapped",
                headerName: "Maturity Date",
                headerTooltip: "Maturity Date",
                field: "maturityDate",
                tooltipValueGetter: this.formatService.formatDate("maturityDate"),
                valueGetter: this.formatService.formatDate("maturityDate"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 150
            }

        ] as ColDef[];
    }

    resizeColumns(event: any) {
        if (this.gridApi != null) {
            this.gridApi.autoSizeAllColumns();
        }
    }

    emailFormatter(): any {
        return (obj: any) => {
            return obj.data.bankContactEmail == null || obj.data.bankContactEmail == "" ? "N/A" : obj.data.bankContactEmail;
        };
    }
    
}
